import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { CheckCircle2, ShieldCheck, Star, Camera, Upload, Loader2 } from 'lucide-react';
import { US_STATES, SPECIALTIES, LICENSE_TYPES } from './constants';

const BecomeSupervisor = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState('featured');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: '', license_type: 'LCSW-S', license_number: '', license_state: 'Texas',
    license_expiration: '', city: '', zip_code: '', delivery: 'both', supervision_type: 'both',
    years_experience: '', price_per_session: '', group_price_per_session: '', display_rate: false, allow_contact: true, bio: '', accepting_new: true, availability: '',
    contact_email: '', phone: '', website: '', populations_served: '', contact_preference: 'message', consent: false, photo_url: '',
  });
  const [specialties, setSpecialties] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    document.title = 'Become a Listed Supervisor | I Need A Social Worker';
    axios.get(`${API_URL}/marketplace/supervisor-plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (s) => setSpecialties((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB.'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/supervisor/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('photo_url', res.data.photo_url);
      toast.success('Photo uploaded.');
    } catch (err) { toast.error('Could not upload photo. Please try a JPG or PNG.'); } finally { setUploadingPhoto(false); }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.info('Create a free supervisor account first.');
      navigate('/register?type=supervisor');
      return;
    }
    if (!form.full_name || !form.license_type || !form.license_state) {
      toast.error('Please complete your name, license type and state.');
      return;
    }
    if (!form.license_number) {
      toast.error('Please enter your Texas license number (required for BHEC verification).');
      return;
    }
    if (!form.consent) {
      toast.error('Please agree to publish your profile to continue.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/supervisor/profile`, {
        ...form,
        years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
        price_per_session: form.price_per_session ? parseFloat(form.price_per_session) : null,
        group_price_per_session: form.group_price_per_session ? parseFloat(form.group_price_per_session) : null,
        display_rate: !!form.display_rate,
        allow_contact: form.allow_contact !== false,
        specialties,
        populations_served: form.populations_served ? form.populations_served.split(',').map((x) => x.trim()).filter(Boolean) : [],
        states_covered: [form.license_state],
        languages: ['English'],
      });
      const res = await axios.post(`${API_URL}/marketplace/supervisor/checkout/stripe`, {
        plan_id: selectedPlan, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Badge className="bg-secondary/15 text-secondary mb-3">Supervisor Network</Badge>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary">Become a Listed Supervisor</h1>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">Reach social workers seeking clinical supervision toward their LCSW. Simple plans. More visibility. More supervisees.</p>
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {['basic', 'featured', 'annual'].map((id) => {
            const p = plans[id];
            if (!p) return null;
            const active = selectedPlan === id;
            return (
              <button key={id} type="button" onClick={() => setSelectedPlan(id)} data-testid={`plan-${id}`}
                className={`text-left rounded-2xl border-2 p-6 transition-all ${active ? 'border-secondary shadow-lg bg-white' : 'border-gray-200 bg-white hover:border-secondary/40'}`}>
                {id === 'featured' && <Badge className="bg-secondary text-white mb-2 flex items-center gap-1 w-fit"><Star className="w-3 h-3" /> Most Popular</Badge>}
                <h3 className="font-serif text-lg font-bold text-primary">{p.name}</h3>
                <p className="text-3xl font-bold text-primary mt-2">${p.price}<span className="text-sm text-gray-500 font-normal">/{p.billing === 'annual' ? 'year' : 'month'}</span></p>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> {f}</li>)}
                </ul>
                <div className={`mt-4 text-center py-2 rounded-lg text-sm font-semibold ${active ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600'}`}>{active ? 'Selected' : 'Select'}</div>
              </button>
            );
          })}
        </div>

        {/* Profile form */}
        <div className="bg-white rounded-2xl border p-6 sm:p-8">
          <h2 className="font-serif text-xl font-bold text-primary mb-1 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-secondary" /> Your Supervisor Profile</h2>
          <p className="text-sm text-gray-500 mb-6">Our team verifies every supervisor's credentials before the listing goes live.</p>

          {/* Profile photo */}
          <div className="flex items-center gap-4 mb-6" data-testid="sup-photo-section">
            <div className="w-20 h-20 rounded-full bg-gray-100 border overflow-hidden flex items-center justify-center flex-shrink-0">
              {form.photo_url
                ? <img src={form.photo_url} alt="Supervisor" className="w-full h-full object-cover" data-testid="sup-photo-preview" />
                : <Camera className="w-7 h-7 text-gray-300" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Profile photo</p>
              <p className="text-xs text-gray-500 mb-2">A friendly headshot helps supervisees connect. JPG or PNG, up to 5MB.</p>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-primary border border-primary/40 rounded-md px-3 py-1.5 cursor-pointer hover:bg-primary/5" data-testid="sup-photo-upload-label">
                {uploadingPhoto ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> {form.photo_url ? 'Change Photo' : 'Upload Photo'}</>}
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="sup-photo-input" />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name *"><Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="sup-full-name" /></Field>
            <Field label="License type *">
              <SelectBox value={form.license_type} onChange={(v) => set('license_type', v)} options={LICENSE_TYPES} testid="sup-license-type" />
            </Field>
            <Field label="License number"><Input value={form.license_number} onChange={(e) => set('license_number', e.target.value)} data-testid="sup-license-number" /></Field>
            <Field label="State *">
              <SelectBox value={form.license_state} onChange={(v) => set('license_state', v)} options={US_STATES} testid="sup-state" />
            </Field>
            <Field label="License expiration"><Input placeholder="MM/DD/YYYY" value={form.license_expiration} onChange={(e) => set('license_expiration', e.target.value)} data-testid="sup-expiration" /></Field>
            <Field label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="sup-city" /></Field>
            <Field label="ZIP code"><Input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} data-testid="sup-zip" /></Field>
            <Field label="Online or In Person">
              <SelectBox value={form.delivery} onChange={(v) => set('delivery', v)} options={[{ value: 'both', label: 'Both' }, { value: 'online', label: 'Online' }, { value: 'in_person', label: 'In Person' }]} testid="sup-delivery" />
            </Field>
            <Field label="Individual or Group">
              <SelectBox value={form.supervision_type} onChange={(v) => set('supervision_type', v)} options={[{ value: 'both', label: 'Both' }, { value: 'individual', label: 'Individual' }, { value: 'group', label: 'Group' }]} testid="sup-supervision-type" />
            </Field>
            <Field label="Years of experience"><Input type="number" value={form.years_experience} onChange={(e) => set('years_experience', e.target.value)} data-testid="sup-experience" /></Field>
            <Field label="Supervision rate display">
              <SelectBox value={form.display_rate ? 'show' : 'contact'} onChange={(v) => set('display_rate', v === 'show')} options={[{ value: 'contact', label: 'Contact me for supervision rates' }, { value: 'show', label: 'Display my supervision rate' }]} testid="sup-display-rate" />
            </Field>
            {form.display_rate && (
              <>
                <Field label="Individual rate ($ per supervision session)"><Input type="number" value={form.price_per_session} onChange={(e) => set('price_per_session', e.target.value)} data-testid="sup-price" /></Field>
                <Field label="Group rate ($ per group supervision session)"><Input type="number" value={form.group_price_per_session} onChange={(e) => set('group_price_per_session', e.target.value)} data-testid="sup-group-price" /></Field>
              </>
            )}
            <Field label="Availability"><Input placeholder="e.g. Weekdays & evenings" value={form.availability} onChange={(e) => set('availability', e.target.value)} data-testid="sup-availability" /></Field>
            <Field label="Allow users to contact me through INASW">
              <SelectBox value={form.allow_contact === false ? 'no' : 'yes'} onChange={(v) => set('allow_contact', v === 'yes')} options={[{ value: 'yes', label: 'Yes — show Contact Supervisor' }, { value: 'no', label: 'No' }]} testid="sup-allow-contact" />
            </Field>
            <Field label="Contact preference">
              <SelectBox value={form.contact_preference} onChange={(v) => set('contact_preference', v)} options={[{ value: 'message', label: 'Secure message' }, { value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' }]} testid="sup-contact-pref" />
            </Field>
            <Field label="Contact email"><Input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} data-testid="sup-contact-email" /></Field>
            <Field label="Website"><Input value={form.website} onChange={(e) => set('website', e.target.value)} data-testid="sup-website" /></Field>
          </div>
          <Field label="Populations served (comma separated)" className="mt-4">
            <Input placeholder="e.g. Adults, Veterans, Families" value={form.populations_served} onChange={(e) => set('populations_served', e.target.value)} data-testid="sup-populations" />
          </Field>
          <Field label="Biography" className="mt-4">
            <Textarea rows={4} value={form.bio} onChange={(e) => set('bio', e.target.value)} placeholder="Describe your supervision approach, experience, and who you help…" data-testid="sup-bio" />
          </Field>
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Specialties</p>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => (
                <button key={s} type="button" onClick={() => toggleSpec(s)} data-testid={`spec-${s}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${specialties.includes(s) ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-300'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-4">
            <Checkbox checked={form.accepting_new} onCheckedChange={(v) => set('accepting_new', !!v)} data-testid="sup-accepting" />
            I am currently accepting new supervisees
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mt-3 bg-gray-50 border rounded-lg p-3">
            <Checkbox checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} data-testid="sup-consent" className="mt-0.5" />
            <span>I confirm I am a real, currently-licensed supervisor and I consent to I Need A Social Worker publishing this profile after my license information is verified through the Texas BHEC public license-verification system. I understand this is a directory listing and not an endorsement.</span>
          </label>

          <Button className="w-full mt-6 bg-secondary hover:bg-secondary-hover text-white h-11" onClick={handleSubmit} disabled={saving} data-testid="submit-supervisor-btn">
            {saving ? 'Redirecting to payment…' : `Submit & Continue to Payment (${plans[selectedPlan] ? '$' + plans[selectedPlan].price : ''})`}
          </Button>
          <p className="text-xs text-gray-400 mt-3 text-center">
            You'll be charged for your chosen plan. Your listing goes live once your credentials are verified.
          </p>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
    {children}
  </div>
);

const SelectBox = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const lab = typeof o === 'string' ? o : o.label;
        return <SelectItem key={val} value={val}>{lab}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

export default BecomeSupervisor;
